import { Box, IconButton, Link, Tooltip, Typography } from '@mui/material';
import { Pencil } from 'lucide-react';
import { useCallback, useEffect, useMemo } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useRecoilValue, useSetRecoilState } from 'recoil';
import AvailableImageItem from '~/components/ImageItem/AvailableImageItem';
import ImageChooserButton from '~/components/ImageItem/ImageChooserButton';
import StructureSetItem from '~/components/Structures/StructureSetItem';
import { AvailableStructureSetView } from '~/domain/projections/views';
import { useAvailableStructureSetsView } from '~/hooks/domain/useAvailableStructureSetsView';
import { useImageDatasetActions } from '~/hooks/dataset/useImageDatasetActions';
import { useStructureSetDeletion } from '~/hooks/dataset/useStructureSetDeletion';
import usePrimaryImage, { AvailableImage } from '~/hooks/image/usePrimaryImage';
import useSecondaryImage from '~/hooks/image/useSecondaryImage';
import useConfirmedSelection from '~/hooks/datasetSelection/useConfirmedSelection';
import DatasetSelect from './DatasetSelect';
import { groupStructureSetsByScan } from './groupStructureSetsByScan';
import { usePreselectFirst } from './usePreselectFirst';
import useAvailableDoses, {
    AvailableDose,
} from '~/hooks/dose/useAvailableDoses';
import useViewportEnvironment from '~/hooks/useViewportEnvironment';
import { currentDoseIdState } from '~/state/atoms/dose/doseAtoms';
import { selectedPatientIdState } from '~/state/atoms/patientAtoms';
import { currentStructureSetIdState } from '~/state/atoms/structureSet/structureSetAtoms';
import { workflowModalState, WorkflowTypes } from '~/state/atoms/viewerAtoms';
import DoseList from '../SetupStep/DatasetList/DoseList';
import RegistrationList from '../SetupStep/DatasetList/RegistrationList';
import UnloadedDatasetList from '../SetupStep/DatasetList/UnloadedDatasetList';
import { useReformatImage } from '../SetupStep/useReformatImage';
import {
    replanningRunOutputsState,
    replanningScansState,
} from '~/state/atoms/adaptiveReplanningAtoms';

interface ReplanningDatasetListProps {
    onEditStructureSet?: (set: AvailableStructureSetView) => void;
}

export default function ReplanningDatasetList({
    onEditStructureSet,
}: ReplanningDatasetListProps = {}) {
    const intl = useIntl();
    const setWorkflowModal = useSetRecoilState(workflowModalState);
    const { primaryImage, availableImages, handleChangePrimaryImage } =
        usePrimaryImage(true);
    const structureSets = useAvailableStructureSetsView(true);
    const currentStructureSetId = useRecoilValue(currentStructureSetIdState);
    const { pickStructureSet } = useConfirmedSelection();
    const { openReformat, reformatDialog } = useReformatImage();
    const { selectedSecondaryImage, handleChangeSecondaryImage } =
        useSecondaryImage();
    const imageActions = useImageDatasetActions();
    const setDeletion = useStructureSetDeletion();
    const { setEnvironmentDose } = useViewportEnvironment();
    const selectedPatientId = useRecoilValue(selectedPatientIdState);
    const currentDoseId = useRecoilValue(currentDoseIdState);
    const allDoses = useAvailableDoses(true);

    const primarySeriesUid = primaryImage?.seriesUid;
    const images: AvailableImage[] = useMemo(
        () =>
            [...availableImages].sort(
                (a, b) =>
                    Number(b.seriesInstanceUID === primarySeriesUid) -
                    Number(a.seriesInstanceUID === primarySeriesUid)
            ),
        [availableImages, primarySeriesUid]
    );

    const displayedTargetSeriesUid = selectedSecondaryImage
        ? selectedSecondaryImage.seriesUid
        : images.find(
              (candidate) => candidate.seriesInstanceUID !== primarySeriesUid
          )?.seriesInstanceUID;
    const setScans = useSetRecoilState(replanningScansState);
    const { propagatedUid, pendingPropagatedUid } = useRecoilValue(
        replanningRunOutputsState
    );
    useEffect(() => {
        // Once a run exists (or is running) the scan roles are fixed; a later display change must not re-derive them.
        if (propagatedUid || pendingPropagatedUid) {
            return;
        }
        setScans((current) =>
            current.sourceSeriesUid === primarySeriesUid &&
            current.targetSeriesUid === displayedTargetSeriesUid
                ? current
                : {
                      sourceSeriesUid: primarySeriesUid,
                      targetSeriesUid: displayedTargetSeriesUid,
                  }
        );
    }, [
        primarySeriesUid,
        displayedTargetSeriesUid,
        propagatedUid,
        pendingPropagatedUid,
        setScans,
    ]);

    const changeRole = (isSource: boolean, chosen: AvailableImage) => {
        if (isSource) {
            if (
                chosen.imageObject &&
                chosen.imageObject.dicomUid === selectedSecondaryImage?.dicomUid
            ) {
                handleChangeSecondaryImage(null);
            }
            void handleChangePrimaryImage(chosen);
            return;
        }
        // Choosing the target never clears it: an unloaded choice or the source itself is ignored.
        if (
            chosen.seriesInstanceUID === primarySeriesUid ||
            !chosen.imageObject
        ) {
            return;
        }
        handleChangeSecondaryImage(chosen.imageObject);
    };

    // Not via the secondary image: fusing across frames of reference creates a registration.
    const showImage = (image: AvailableImage) => {
        const drawnOnIt = bySeriesUid.get(image.seriesInstanceUID)?.[0];
        if (drawnOnIt) {
            pickStructureSet(drawnOnIt);
            return;
        }
        void handleChangePrimaryImage(image);
    };

    const sourceDoses = useMemo(
        () =>
            allDoses.filter((dose) => dose.imageUid === primaryImage?.dicomUid),
        [allDoses, primaryImage]
    );
    const selectedDoseSopInstanceUid = sourceDoses.find(
        (dose) => dose.id === currentDoseId
    )?.sopInstanceUID;
    const selectDose = useCallback(
        (dose: AvailableDose) => {
            if (dose.item) {
                setEnvironmentDose([dose.item], selectedPatientId ?? '');
            }
        },
        [setEnvironmentDose, selectedPatientId]
    );

    const { bySeriesUid, unattached } = groupStructureSetsByScan(
        images.map((image) => image.seriesInstanceUID),
        structureSets
    );

    const sourceSets = primarySeriesUid
        ? (bySeriesUid.get(primarySeriesUid) ?? [])
        : [];
    usePreselectFirst({
        options: sourceSets,
        hasSelection: sourceSets.some(
            (set) => set.sopInstanceUid === currentStructureSetId
        ),
        onSelect: pickStructureSet,
    });
    // A dose selects its own structure set, so only the chosen set's doses are preselected.
    const dosesForSelectedSet = sourceDoses.filter(
        (dose) => dose.item?.structureSet?.dicomUid === currentStructureSetId
    );
    usePreselectFirst({
        options: dosesForSelectedSet,
        hasSelection: !!selectedDoseSopInstanceUid,
        onSelect: selectDose,
    });

    const renderSet = (set: AvailableStructureSetView) => (
        <Box
            key={set.sopInstanceUid}
            sx={{
                pl: 2,
                pr: 1,
                ml: 1.5,
                borderLeft: '1px solid',
                borderColor: 'divider',
            }}
        >
            <Box display="flex" alignItems="center" gap={0.5}>
                <Box flex={1} minWidth={0}>
                    <StructureSetItem
                        structureSet={set}
                        isActive={currentStructureSetId === set.sopInstanceUid}
                        allowReselect
                        onClick={() => pickStructureSet(set)}
                        onDelete={
                            setDeletion.canRemove(set)
                                ? () => setDeletion.remove(set)
                                : undefined
                        }
                    />
                </Box>
                {onEditStructureSet && (
                    <Tooltip
                        title={intl.formatMessage({
                            id: 'replanning.selection.editStructures',
                        })}
                    >
                        <IconButton
                            size="small"
                            onClick={() => {
                                pickStructureSet(set);
                                onEditStructureSet(set);
                            }}
                            data-testid={`replanning-edit-set-${set.sopInstanceUid}`}
                            sx={{ p: 0.25, flexShrink: 0 }}
                        >
                            <Pencil size={12} />
                        </IconButton>
                    </Tooltip>
                )}
            </Box>
        </Box>
    );

    return (
        <>
            {images.map((image) => {
                const attached = bySeriesUid.get(image.seriesInstanceUID) ?? [];
                const selectedSourceSet =
                    attached.find(
                        (set) => set.sopInstanceUid === currentStructureSetId
                    ) ?? attached[0];
                const isPrimary = image.seriesInstanceUID === primarySeriesUid;
                const isSecondary =
                    !isPrimary &&
                    image.seriesInstanceUID === displayedTargetSeriesUid;
                const roleId = isPrimary
                    ? 'replanning.role.primary'
                    : isSecondary
                      ? 'replanning.role.secondary'
                      : undefined;
                return (
                    <Box
                        key={image.seriesInstanceUID}
                        sx={{
                            position: 'relative',
                            pb: 0.5,
                            borderTop: '1px solid',
                            borderColor: isPrimary ? 'primary.dark' : 'divider',
                            bgcolor: isPrimary
                                ? 'rgba(144, 202, 249, 0.04)'
                                : 'transparent',
                            '&::before': {
                                content: '""',
                                position: 'absolute',
                                left: 0,
                                top: 0,
                                bottom: 0,
                                width: 2,
                                bgcolor: isPrimary
                                    ? 'primary.main'
                                    : 'transparent',
                                zIndex: 1,
                            },
                            mt: isPrimary ? 0 : 1,
                            transition: 'background-color 0.15s ease-in-out',
                        }}
                    >
                        {roleId && (
                            <Box
                                px={1.5}
                                pt={1}
                                pb={0.25}
                                display="flex"
                                alignItems="center"
                                justifyContent="space-between"
                                gap={1}
                            >
                                <Typography
                                    fontSize={10}
                                    fontWeight={700}
                                    letterSpacing={0.6}
                                    color={
                                        isPrimary
                                            ? 'primary.main'
                                            : 'text.secondary'
                                    }
                                    sx={{ textTransform: 'uppercase' }}
                                >
                                    <FormattedMessage id={roleId} />
                                </Typography>
                                {isPrimary && (
                                    <ImageChooserButton
                                        images={availableImages}
                                        selectedSeriesUid={
                                            image.seriesInstanceUID
                                        }
                                        onChoose={(chosen) =>
                                            changeRole(true, chosen)
                                        }
                                        ariaLabel={intl.formatMessage({
                                            id: 'replanning.role.changeSource',
                                        })}
                                        testId="replanning-choose-source"
                                    />
                                )}
                                {isSecondary && (
                                    <ImageChooserButton
                                        images={availableImages}
                                        selectedSeriesUid={
                                            image.seriesInstanceUID
                                        }
                                        onChoose={(chosen) =>
                                            changeRole(false, chosen)
                                        }
                                        ariaLabel={intl.formatMessage({
                                            id: 'replanning.role.changeTarget',
                                        })}
                                        testId="replanning-choose-target"
                                    />
                                )}
                            </Box>
                        )}
                        <AvailableImageItem
                            image={image}
                            isSecondary={isSecondary}
                            isSelected={isPrimary}
                            onSelect={
                                image.needsReformat
                                    ? undefined
                                    : () => showImage(image)
                            }
                            onReformat={() => openReformat(image)}
                            onDownload={
                                !image.imageObject && !image.rawSeries
                                    ? () => handleChangePrimaryImage(image)
                                    : undefined
                            }
                            onRename={
                                imageActions.canRename(image)
                                    ? () => imageActions.rename(image)
                                    : undefined
                            }
                            onDelete={
                                imageActions.canRemove(image)
                                    ? () => imageActions.remove(image)
                                    : undefined
                            }
                        />
                        {isPrimary ? (
                            <Box
                                display="flex"
                                flexDirection="column"
                                gap={0.5}
                                sx={{
                                    pl: 2,
                                    pr: 1,
                                    ml: 1.5,
                                    py: 0.5,
                                    borderLeft: '1px solid',
                                    borderColor: 'divider',
                                }}
                            >
                                <DatasetSelect
                                    label={intl.formatMessage({
                                        id: 'replanning.source.structures',
                                    })}
                                    options={attached.map((set) => ({
                                        uid: set.sopInstanceUid,
                                        name:
                                            set.displayName ||
                                            intl.formatMessage({
                                                id: 'structureSet',
                                            }),
                                    }))}
                                    selectedUid={currentStructureSetId ?? ''}
                                    onSelect={(uid) => {
                                        const chosen = attached.find(
                                            (set) => set.sopInstanceUid === uid
                                        );
                                        if (chosen) {
                                            pickStructureSet(chosen);
                                        }
                                    }}
                                    testId="replanning-source-structures"
                                    action={
                                        onEditStructureSet &&
                                        selectedSourceSet && (
                                            <Tooltip
                                                title={intl.formatMessage({
                                                    id: 'replanning.selection.editStructures',
                                                })}
                                            >
                                                <IconButton
                                                    size="small"
                                                    onClick={() =>
                                                        onEditStructureSet(
                                                            selectedSourceSet
                                                        )
                                                    }
                                                    data-testid={`replanning-edit-set-${selectedSourceSet.sopInstanceUid}`}
                                                    sx={{ p: 0.25 }}
                                                >
                                                    <Pencil size={12} />
                                                </IconButton>
                                            </Tooltip>
                                        )
                                    }
                                />
                                <DatasetSelect
                                    label={intl.formatMessage({
                                        id: 'replanning.source.priorDose',
                                    })}
                                    options={sourceDoses.map((dose) => ({
                                        uid: dose.sopInstanceUID,
                                        name:
                                            dose.label ??
                                            intl.formatMessage({ id: 'dose' }),
                                    }))}
                                    selectedUid={selectedDoseSopInstanceUid}
                                    onSelect={(uid) => {
                                        const chosen = sourceDoses.find(
                                            (dose) =>
                                                dose.sopInstanceUID === uid
                                        );
                                        if (chosen) {
                                            selectDose(chosen);
                                        }
                                    }}
                                    testId="replanning-source-dose"
                                />
                            </Box>
                        ) : (
                            <>
                                {attached.length > 0 && attached.map(renderSet)}
                                <Box
                                    sx={{
                                        pl: 2,
                                        pr: 1,
                                        ml: 1.5,
                                        borderLeft: '1px solid',
                                        borderColor: 'divider',
                                    }}
                                >
                                    <DoseList
                                        onlyForImageUid={
                                            image.imageObject?.dicomUid
                                        }
                                        hideHeading
                                    />
                                </Box>
                            </>
                        )}
                        {attached.length === 0 && isPrimary && (
                            <Box
                                display="flex"
                                alignItems="center"
                                gap={0.75}
                                sx={{
                                    pl: 2,
                                    ml: 1.5,
                                    py: 0.5,
                                    borderLeft: '1px solid',
                                    borderColor: 'divider',
                                }}
                            >
                                <Typography
                                    fontSize={11}
                                    color="text.disabled"
                                    fontStyle="italic"
                                >
                                    <FormattedMessage id="replanning.scan.noStructures" />
                                </Typography>
                                <Link
                                    component="button"
                                    type="button"
                                    underline="always"
                                    variant="caption"
                                    onClick={() =>
                                        setWorkflowModal({
                                            open: true,
                                            page: WorkflowTypes.CONTOUR,
                                        })
                                    }
                                    data-testid="replanning-generate-structures"
                                >
                                    <FormattedMessage id="replanning.scan.generateStructures" />
                                </Link>
                            </Box>
                        )}
                    </Box>
                );
            })}

            {unattached.length > 0 && (
                <Box borderTop="1px solid" borderColor="divider">
                    <Typography
                        fontSize={12}
                        color="text.secondary"
                        px={1.5}
                        pt={1.5}
                        pb={0.5}
                    >
                        <FormattedMessage id="replanning.scan.unattached" />
                    </Typography>
                    {unattached.map(renderSet)}
                </Box>
            )}

            <RegistrationList />
            <UnloadedDatasetList />

            {imageActions.dialogs}
            {setDeletion.dialog}
            {reformatDialog}
        </>
    );
}
